import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const getCurrentMonthInput = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const toYmd = (dateValue) => (dateValue ? dateValue.replace(/-/g, '') : '');
const toYm = (monthValue) => (monthValue ? monthValue.replace(/-/g, '') : '');

const getNextMonthStartYmd = (ym) => {
  if (!ym || ym.length !== 6) return '';
  const year = Number(ym.slice(0, 4));
  const month = Number(ym.slice(4, 6));
  if (Number.isNaN(year) || Number.isNaN(month)) return '';
  const next = new Date(year, month, 1);
  return `${next.getFullYear()}${String(next.getMonth() + 1).padStart(2, '0')}01`;
};

const QuizSetList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchYm, setSearchYm] = useState(() => getCurrentMonthInput());
  const [searchLanguage, setSearchLanguage] = useState('ko');

  const normalizedYm = useMemo(() => toYm(searchYm), [searchYm]);

  const fetchQuizSets = async (ym, language) => {
    setLoading(true);
    setErrorMessage('');
    try {
      let query = supabase
        .from('quiz_sets')
        .select('id, quiz_date, title, language, is_active, qt_ymd, created_at')
        .order('quiz_date', { ascending: false });

      if (ym) {
        const from = `${ym}01`;
        const next = getNextMonthStartYmd(ym);
        query = query.gte('qt_ymd', from);
        if (next) query = query.lt('qt_ymd', next);
      }
      if (language) query = query.eq('language', language);

      const { data, error } = await query;
      if (error) throw error;
      setItems(data ?? []);
    } catch (error) {
      setErrorMessage(error.message || '목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizSets(normalizedYm, searchLanguage || undefined);
  }, []);

  const handleSearch = () => {
    fetchQuizSets(normalizedYm, searchLanguage || undefined);
  };

  const handleReset = () => {
    const currentMonth = getCurrentMonthInput();
    setSearchYm(currentMonth);
    setSearchLanguage('ko');
    fetchQuizSets(toYm(currentMonth), 'ko');
  };

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">퀴즈 관리</h6>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/quiz/new')} disabled={loading}>
          신규 등록
        </button>
      </div>

      <div className="card-body">
        <div className="form-row align-items-end mb-3">
          <div className="form-group col-md-2 mb-2 mb-md-0">
            <label className="small font-weight-bold mb-1">검색년월</label>
            <input
              type="month"
              className="form-control form-control-sm"
              value={searchYm}
              onChange={(event) => setSearchYm(event.target.value)}
            />
          </div>
          <div className="form-group col-md-2 mb-2 mb-md-0">
            <label className="small font-weight-bold mb-1">언어</label>
            <select
              className="form-control form-control-sm"
              value={searchLanguage}
              onChange={(event) => setSearchLanguage(event.target.value)}
            >
              <option value="">전체</option>
              <option value="ko">ko</option>
              <option value="en">en</option>
              <option value="ja">ja</option>
              <option value="zh">zh</option>
            </select>
          </div>
          <div className="form-group col-md-3 mb-0 d-flex" style={{ gap: 8 }}>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleSearch} disabled={loading}>
              검색
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset} disabled={loading}>
              초기화
            </button>
          </div>
        </div>

        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-bordered">
            <thead className="thead-light">
              <tr>
                <th style={{ width: '100px' }}>일자</th>
                <th style={{ width: '200px' }}>제목</th>
                <th style={{ width: '80px' }}>언어</th>
                <th style={{ width: '80px' }}>활성</th>
                <th style={{ width: '200px' }}>등록일</th>
                <th style={{ width: '90px' }}>액션</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    조회 중...
                  </td>
                </tr>
              ) : items.length ? (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.quiz_date}</td>
                    <td>{item.title}</td>
                    <td>{item.language || '-'}</td>
                    <td>{item.is_active || 'N'}</td>
                    <td>{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => navigate(`/quiz/${item.id}`)}
                      >
                        수정
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    조회된 데이터가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuizSetList;
